import {
    Component,
    Input,
    forwardRef,
    OnInit,
    OnChanges,
    ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { EllipsisPipe } from '../../../pipes/ellipsis.pipe';
import { ExpandableRadioButton } from '../../../../core/models/expandable-radio-button';
import { CommonModule } from '@angular/common';
import { SafeHtml } from '@angular/platform-browser';

/**
 * Renders a radio button and text inside of an expansion panel. Essentially, this turns the entire expansion panel
 * into a radio button. The purpose of this component is to provide a clear way to display a lot of text (expanded or not)
 * while also allowing the user to associate and treat the radio button and text as a single entity.
 */
@Component({
    selector: 'app-expandable-radio-button',
    templateUrl: './expandable-radio-button.component.html',
    styleUrls: ['./expandable-radio-button.component.scss'],
    imports: [MatRadioModule, MatExpansionModule, MatIconModule, CommonModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        EllipsisPipe,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ExpandableRadioButtonComponent),
            multi: true,
        },
    ],
})
export class ExpandableRadioButtonComponent
    implements ControlValueAccessor, OnInit, OnChanges
{
    /**
     * The model for the radio button.
     */
    @Input() model!: ExpandableRadioButton;

    /**
     * Whether the radio button is disabled.
     */
    @Input() isDisabled: boolean = false;
    @Input() isInvalid: boolean = false;

    constructor(private readonly ellipsisPipe: EllipsisPipe) {}

    isChecked: boolean = false;
    needsExpansion: boolean = false;
    isExpanded: boolean = false;

    private onChange: (value: string | null) => void = () => {};
    private onTouched: () => void = () => {};

    ngOnInit() {
        this.updateShouldShowFullText();
    }

    ngOnChanges() {
        this.updateShouldShowFullText();
    }

    getLabelContent(): SafeHtml {
        let content: string;

        if (this.isExpanded || !this.needsExpansion) {
            content = this.model.label;
        } else {
            content = this.ellipsisPipe.transform(
                this.model.label,
                this.model.ellipsisLength!,
            );
        }

        return content;
    }

    private updateShouldShowFullText() {
        this.needsExpansion = !this.isShowingAllContent();
    }

    private isShowingAllContent(): boolean {
        if (
            this.model.ellipsisLength &&
            this.model.label.length > this.model.ellipsisLength
        ) {
            return false;
        } else {
            return true;
        }
    }

    writeValue(value: string | null): void {
        this.isChecked = this.model.value === value;
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    onChanged(value: string | null): void {
        this.isChecked = true;
        this.onChange(value);
        this.onTouched();
    }

    toggleExpand(event: Event): void {
        event.stopPropagation();
        this.isExpanded = !this.isExpanded;
    }
}
